import React, { useLayoutEffect, useRef } from 'react';
import { conMiles, conMilesYDecimales, soloDigitos } from './formatos';

/**
 * Campo para un monto: con separador de miles en pantalla. En pesos (decimales 0) solo
 * acepta enteros y entrega los dígitos sin puntos, que es lo que recibe el servidor. En
 * otra moneda (decimales 2) acepta la coma decimal y entrega el número con punto
 * («3.400,12» → «3400.12»). Acepta el signo menos, porque los datos se guardan como
 * vienen; la revisión avisa donde la DJ pide el monto en positivo.
 *
 * @param {Object} props
 * @param {string} props.valor - Monto sin puntos de miles (con punto decimal si tiene decimales)
 * @param {number} props.largo - Máximo de dígitos enteros (15 en el importador)
 * @param {number} [props.decimales=0] - Decimales permitidos: 0 en pesos, 2 en otra moneda
 * @param {Function} props.onCambiar - Recibe el monto nuevo
 */
const CampoMonto = ({ valor, largo, decimales = 0, onCambiar }) => {
    const campo = useRef(null);
    const digitosAntesDelCursor = useRef(null);
    const significativo = decimales > 0 ? /[\d,-]/ : /[\d-]/;

    // Al poner los puntos, el cursor queda después del mismo dígito en que estaba
    useLayoutEffect(() => {
        if (digitosAntesDelCursor.current === null) return;
        const texto = campo.current.value;
        let posicion = 0;
        for (let vistos = 0; posicion < texto.length && vistos < digitosAntesDelCursor.current; posicion++) {
            if (significativo.test(texto[posicion])) vistos++;
        }
        campo.current.setSelectionRange(posicion, posicion);
        digitosAntesDelCursor.current = null;
    });

    const handleChange = (e) => {
        const escrito = e.target.value;
        // Los pesos van enteros: una coma sería decimal
        if (escrito.includes(',')) return;
        const digitos = soloDigitos(escrito);
        if (digitos.replace('-', '').length > largo) return;
        digitosAntesDelCursor.current = soloDigitos(escrito.slice(0, e.target.selectionStart)).length;
        onCambiar(digitos);
    };

    // En otra moneda: «.» son los miles y «,» el decimal, como se escribe en Chile
    const handleChangeConDecimales = (e) => {
        const escrito = e.target.value;
        const [entero, ...resto] = escrito.replace(/\./g, '').split(',');
        if (resto.length > 1) return;
        const digitosEnteros = soloDigitos(entero);
        const digitosDecimales = resto.length === 1 ? resto[0].replace(/\D/g, '') : null;
        if (digitosEnteros.replace('-', '').length > largo) return;
        if (digitosDecimales !== null && digitosDecimales.length > decimales) return;
        digitosAntesDelCursor.current = (escrito.slice(0, e.target.selectionStart).match(/[\d,-]/g) ?? []).length;
        onCambiar(digitosDecimales === null ? digitosEnteros : `${digitosEnteros}.${digitosDecimales}`);
    };

    return (
        <input
            ref={campo}
            className="monto"
            value={decimales > 0 ? conMilesYDecimales(valor) : conMiles(valor)}
            onChange={decimales > 0 ? handleChangeConDecimales : handleChange}
            inputMode={decimales > 0 ? 'decimal' : 'numeric'}
        />
    );
};

export default CampoMonto;
